import { createContext, type FC, type PropsWithChildren, useContext, useMemo } from "react";

import { ErrorView } from "@/components/error-view";
import { Loading } from "@/components/loading-view";

import { DatatableState, defaultDatatableState } from "../components/datatable/datatable-types";
import { type ActiveView } from "../models/active-view-model";
import { compareDatatableStates } from "../models/datatable-state-model";

import { useCreateActiveView } from "./api/use-create-active-view";
import { useGetActiveView } from "./api/use-get-active-view";
import { useUpdateActiveView } from "./api/use-update-active-view";

type CurrentViewContext = {
  currentActiveView?: ActiveView | null | undefined;
  datatableState: DatatableState;
  saveDatatableState: (newDatatableState: DatatableState) => void;
};

const CurrentViewContext = createContext<CurrentViewContext | null>(null);

export interface CurrentViewContextProviderProps extends PropsWithChildren {
  workspaceId: string;
  viewKey: string;
  baseState?: DatatableState;
}

export const CurrentViewContextProvider: FC<CurrentViewContextProviderProps> = ({
  children,
  workspaceId,
  viewKey,
  baseState = defaultDatatableState,
}) => {
  const { activeView, loading, error } = useGetActiveView({ workspaceId, key: viewKey });

  const { updateActiveView } = useUpdateActiveView();
  const { createActiveView, loading: createActiveViewLoading } = useCreateActiveView();

  const datatableState = useMemo(() => {
    if (activeView) {
      return {
        ...activeView,
        columnFilters: activeView.columnFilters.map((filter) => ({
          ...filter,
          value: filter.value ?? null,
        })),
      };
    }
    return baseState;
  }, [activeView, baseState]);

  const saveDatatableState = (newDatatableState: DatatableState) => {
    if (createActiveViewLoading) {
      return;
    }

    // make sure to not uneccessarily update on first load
    if (compareDatatableStates(datatableState, newDatatableState)) {
      return;
    }

    if (!activeView) {
      void createActiveView({
        variables: {
          input: {
            ...newDatatableState,
            workspaceId,
            key: viewKey,
          },
        },
      });
    }

    if (activeView) {
      void updateActiveView({
        variables: {
          input: {
            id: activeView.id,
            ...newDatatableState,
          },
        },
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorView />;
  }

  return (
    <CurrentViewContext.Provider value={{ currentActiveView: activeView, datatableState, saveDatatableState }}>
      {children}
    </CurrentViewContext.Provider>
  );
};

export const useCurrentViewContext = () => {
  const context = useContext(CurrentViewContext);

  if (!context) {
    throw new Error("useCurrentViewContext must be used within a CurrentViewContextProvider");
  }

  return context;
};
